import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { brand } from "@/lib/site";
import LandingHeader from "@/components/LandingHeader";
import { formatBlogDate, setPageSeo } from "@/lib/seo";
import { ArrowRight, ChevronRight } from "lucide-react";

const EYEBROW = "font-eyebrow text-[11px] font-semibold uppercase tracking-[0.18em] sm:text-xs";

function GoldDivider({ className = "max-w-[200px]" }) {
    return (
        <div className={`flex items-center ${className}`}>
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#D4AF37]/80 to-transparent" />
            <span className="px-2.5 text-[10px] leading-none text-[#C9A227]">◆</span>
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#D4AF37]/80 to-transparent" />
        </div>
    );
}

export default function Blog() {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setPageSeo({
            title: `Blog | ${brand.name}`,
            description: "Salon & spa growth tips — bookings, GST billing, memberships, and running a modern Indian salon.",
            canonical: `${window.location.origin}/blog`,
        });
    }, []);

    useEffect(() => {
        api.get("/public/blog", { params: { limit: 24 } })
            .then((r) => setPosts(r.data?.posts || []))
            .catch(() => setPosts([]))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div className="min-h-screen bg-[#FAF9F6] font-['Outfit',system-ui,sans-serif]">
            <LandingHeader />

            <main className="mx-auto max-w-5xl px-5 py-14 sm:px-6 sm:py-20">
                <p className={EYEBROW} style={{ color: "#C9A227" }}>Insights</p>
                <h1 className="mt-2 font-serif text-4xl font-medium tracking-tight text-[#4A0E17] sm:text-5xl">
                    Salon &amp; spa blog
                </h1>
                <GoldDivider className="mt-4" />
                <p className="mt-4 max-w-2xl text-base leading-relaxed text-[#5C4033]/90">
                    Practical guides for Indian salon owners — bookings, billing, staff, and growth.
                </p>

                {loading ? (
                    <p className="mt-12 text-[#8A8178]">Loading articles…</p>
                ) : posts.length === 0 ? (
                    <p className="mt-12 text-[#8A8178]">No articles published yet. Check back soon.</p>
                ) : (
                    <div className="mt-12 grid gap-6 sm:grid-cols-2">
                        {posts.map((post) => (
                            <Link
                                key={post.id}
                                to={`/blog/${post.slug}`}
                                className="group flex h-full flex-col overflow-hidden rounded-2xl border border-[#E8E4DE]/90 bg-white shadow-[0_4px_22px_rgba(45,41,38,0.04)] transition-all duration-300 hover:-translate-y-1 hover:border-[#D4AF37]/40 hover:shadow-[0_16px_40px_rgba(74,14,23,0.08)]"
                            >
                                {post.cover_image_url ? (
                                    <div className="aspect-[16/9] overflow-hidden bg-[#F2EFE9]">
                                        <img
                                            src={post.cover_image_url}
                                            alt=""
                                            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                        />
                                    </div>
                                ) : (
                                    <div className="aspect-[16/9] bg-gradient-to-br from-[#4A0E17] to-[#5A1828]" />
                                )}
                                <div className="flex flex-1 flex-col p-5">
                                    <p className="text-xs font-semibold text-[#8A8178]">
                                        {formatBlogDate(post.published_at || post.created_at)}
                                    </p>
                                    <h2 className="mt-2 font-serif text-xl font-medium text-[#4A0E17] group-hover:text-[#C84B31]">
                                        {post.title}
                                    </h2>
                                    <p className="mt-2 flex-1 text-sm leading-relaxed text-[#8A8178] line-clamp-3">
                                        {post.excerpt}
                                    </p>
                                    <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#C9A227]">
                                        Read article
                                        <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
                                    </span>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}

                <div className="mt-16 rounded-2xl border border-[#E8E4DE] bg-white p-8 text-center">
                    <h2 className="font-serif text-2xl text-[#4A0E17]">Ready to run your salon on {brand.name}?</h2>
                    <p className="mt-2 text-sm text-[#8A8178]">Start your free trial — no credit card required.</p>
                    <Link
                        to="/register"
                        className="mt-5 inline-flex items-center gap-2 rounded-md bg-[#C84B31] px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#A33923]"
                    >
                        Start free trial
                        <ChevronRight className="size-4" />
                    </Link>
                </div>
            </main>
        </div>
    );
}
